import { setKey } from "@assetcrush/reactjs-sdk";
import React, { useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { IntlProvider } from "react-intl";
import { useSelector } from "react-redux";
import { ThemeProvider, createTheme, useMediaQuery } from "@mui/material";
import { AuthWizardHost } from "./components/AuthWizard/AuthWizard";
import FullPageLoading from "./components/FullPageLoading/FullPageLoading";
import Snackbar from "./components/snackbar";
import { anonymousLogin } from "./containers/CountryPage/sagas/saga";
import { getUser, updateUserProfile } from "./containers/SignupPage/sagas/saga";
import Routes from "./routes";
import { useSessionRefreshOnFocus } from "./utilities/sessionRefresh";
import fetchTranslation from "./translation/sagas";
import { getStoredLanguage, normalizeLanguage, setStoredLanguage } from "./utilities/lang";
import { getBrowserCountry } from "./utilities/geo";
import { getCurrentUserId } from "./utilities/auth";
import { apiClient } from "./utilities/apiClient";
import { fontFamilyForLocale, loadFontForLocale } from "./fonts";
import { LanguageWelcome } from "./components/LanguageWelcome/LanguageWelcome";

function App() {
  const { messages, locale } = useSelector((state) => state.intl);
  // CountryPage's `auth` slice handles ANONYMOUS_LOGIN_SUCCEEDED — its
  // isLoading flag flips false the moment the anon-bootstrap POST returns
  // with cookies set, which is the signal we wait on before reading the
  // uid cookie for the snapshot useEffect below.
  const authIsLoading = useSelector((state) => state.auth?.isLoading ?? false);
  const { isLoading, user } = useSelector((state) => state.user);

  // MUI components follow the device color scheme. Tailwind handles the rest
  // via `dark:` variants (darkMode: 'media'). Brand cobalt stays the primary.
  // Typography font follows the active locale so hi/ur swap into the
  // Noto Devanagari/Arabic stacks loaded by `loadFontForLocale`.
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDark ? 'dark' : 'light',
          primary: { main: '#3a6df0' },
        },
        typography: {
          fontFamily: fontFamilyForLocale(locale),
        },
      }),
    [prefersDark, locale],
  );

  // Whenever the locale changes (initial mount, user picks from Settings,
  // signed-in user's stored language hydrates), conditionally load the
  // webfont for that locale and reflect the locale on <html lang="…"> so
  // the [lang="hi"] / [lang="ur"] CSS overrides in index.scss take effect.
  useEffect(() => {
    if (!locale) return;
    loadFontForLocale(locale);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  useEffect(() => {
    setKey('test');
    fetchTranslation();
    // Phase A bootstrap: GET /auth/me on the new backend (cookies travel
    // automatically). If the request fails with 401, mint a fresh anonymous
    // session so every visitor always has *some* identity — matches the
    // legacy Firebase anon-login behavior.
    getUser({ errCallback: () => anonymousLogin() });
  }, []);

  // Language auto-detect. The Settings page's LanguageMenu lets users pick
  // an override later, but most visitors won't bother — so we snapshot the
  // resolved language on the first session where `user.language` is null,
  // PATCH it onto the user row, and the admin dashboard's languageBreakdown
  // can then surface which markets are arriving. Uses getStoredLanguage
  // (cookie → browser default) rather than the raw browser guess so the
  // explicit pick from the LanguageWelcome modal wins over the OS guess
  // when the two disagree (e.g., Spanish browser, user picked English).
  useEffect(() => {
    if (!user?.id) return;
    if (user.language) return;
    const guessed = getStoredLanguage();
    if (!guessed) return;
    updateUserProfile({
      userId: user.id,
      userData: { language: guessed },
      notification: false,
      noUpdate: true,
    }).catch(() => null);
  }, [user]);

  // Browser-side snapshot of country + language onto the user row.
  //
  // The earlier "gate on redux user" pattern (used by the language
  // useEffect above) silently never fires for anonymous users:
  // getUser explicitly dispatches GET_USER_FAILED for anons (per design,
  // so PrivateRoute keeps redirecting them to sign-in), so
  // state.user.user stays `{}` forever for them. Result: every anon
  // visitor's users.country and users.language stayed NULL forever, the
  // concierge had to ask each one for their country, and the bot's
  // "I already know your country" prompt branch had nothing to default
  // from.
  //
  // Snapshot via cookie auth instead. getCurrentUserId() reads the
  // non-httpOnly `uid` cookie, which the backend sets alongside the
  // access JWT for both anon AND signed-in sessions. The PATCH itself
  // identifies the user from `access` cookie sub on the server side —
  // we send no user id in the body — so this works regardless of the
  // redux hydration state.
  //
  // Timing: GET /auth/me 401s for fresh visitors → GET_USER_FAILED
  // flips state.user.isLoading=false BEFORE the anonymousLogin POST
  // runs (and therefore before the uid cookie is set). The useEffect
  // therefore depends on BOTH that signal AND state.auth.isLoading
  // (CountryPage's slice), which flips false on
  // ANONYMOUS_LOGIN_SUCCEEDED — the first moment we're sure both the
  // access and uid cookies are written. snapshotDone ref + sessionStorage
  // stamp belt-and-suspenders against re-fires.
  const snapshotDoneRef = useRef(false);
  useEffect(() => {
    if (snapshotDoneRef.current) return;
    if (isLoading || authIsLoading) return; // wait for both auth paths
    const uid = getCurrentUserId();
    if (!uid) return; // cookie not yet written — wait for next state flip

    snapshotDoneRef.current = true;
    const SNAPSHOT_KEY = "dresez_browser_snapshot_v1";
    try {
      if (window.sessionStorage.getItem(SNAPSHOT_KEY) === uid) return;
    } catch {
      // sessionStorage disabled (private-mode quirks) — still PATCH, just
      // skip the guard. Worst case is one extra PATCH per page reload.
    }
    const country = getBrowserCountry();
    const language = getStoredLanguage();
    const payload = {};
    if (country) payload.country = String(country).toUpperCase();
    if (language) payload.language = language;
    if (Object.keys(payload).length === 0) return;
    apiClient
      .patch("/auth/me", payload)
      .then(() => {
        try {
          window.sessionStorage.setItem(SNAPSHOT_KEY, uid);
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        // Allow retry on the next state flip if the PATCH itself failed
        // (transient network). Don't keep the door open if the failure
        // was a 4xx (auth invalid) — but distinguishing here would
        // require parsing the error, and the worst case is one extra
        // attempt next time the deps change.
        snapshotDoneRef.current = false;
      });
  }, [isLoading, authIsLoading]);

  // When a signed-in user's stored language arrives (sign-in completes, or
  // a fresh device hydrates the user row for the first time), apply it to
  // the UI and mirror it to the cookie. Without this the IntlProvider
  // stays on whatever the cookie / browser said on mount, and the picker
  // appears to disagree with the rendered language.
  useEffect(() => {
    const desired = normalizeLanguage(user?.language);
    if (!desired) return;
    if (desired === locale) return;
    fetchTranslation(desired);
    setStoredLanguage(desired);
  }, [user?.language, locale]);

  // Silently keep the cookie fresh while the user is around — every focus
  // / visibilitychange-visible tick (throttled to 5 min) hits /auth/me,
  // which rotates a near-expiry access via the apiClient interceptor.
  useSessionRefreshOnFocus();

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider messages={messages} locale={locale}>
          <FullPageLoading />
        </IntlProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <IntlProvider messages={messages} locale={locale}>
        <Snackbar />
        {/* Noindex any non-prod build — local dev + any preview/branch build
            that isn't explicitly REACT_APP_ENV=PROD. */}
        {process.env.REACT_APP_ENV !== 'PROD' && <Helmet>
          <meta name="robots" content="noindex"></meta>
        </Helmet>}
        <Routes />
        <AuthWizardHost />
        {/* First-launch language picker — self-skips when there's a cookie,
            the browser is English, or user.language is already set. */}
        <LanguageWelcome />
      </IntlProvider>
    </ThemeProvider>
  );
}

export default App;
